﻿import $ from 'jquery';
import 'jquery-highlight';
import Element from '../utilities/element';

declare global {
  interface JQuery {
    filteritems: (option, args) => any;
  }
}

export class FilterItems {
  public static get attribute(): string { return 'filteritems'; }
  public static get wireup(): string { return FilterItems.attribute.asDataSelector() }

  private input: Element;
  private key: string;
  private options: FilterItemsOptions = {};
  private selectedClass: string;
  private get $items(): JQuery {
    return $('[data-fi="' + this.key + '"]');
  }
  private emptySelector: string = '[data-fi-empty]';

  public constructor($input: JQuery, options: any) {
    this.input = new Element($input);
    this.key = $input.data(FilterItems.attribute);
    this.options = this.getOptions(options);

    if (this.key == null)
      throw new Error('key must be provided.')

    this.selectedClass = 'fi-selected';
    if (this.options.selectedClass)
      this.selectedClass += ' ' + this.options.selectedClass;

    this.emptyFilter();
    this.wireupEvents();
  }

  private getOptions(options: any): FilterItemsOptions {
    let mapped = FilterItems.optionMap(this.input.$element.data());
    return $.extend({}, this.options, options, mapped);
  }

  private wireupEvents(): void {
    this.input.$userFacingElement.on('input.rustig.filteritems', () => this.filterItems());
  }

  public filterItems(): void {
    let $itemList = this.$items;
    let filterText = this.input.value();

    if (!filterText) {
      this.showMatches($itemList);
      let $selected = $itemList.filter(this.emptySelector).first().addClass(this.selectedClass);

      this.triggerOnFiltered(filterText, $itemList, $(), $selected);

      return;
    }

    let filter = '\\b' + this.escapeRegExp(filterText).toLowerCase();
    let regex = new RegExp(filter);

    let lowestSearchIndex = null;
    let selectIndex = 0;
    let index = 0;
    let $matches = $itemList.filter(function () {
      let item: Element = new Element($(this));
      index++;
      let searchIndex = (<string>(item.text())).toLowerCase().search(regex);
      if (searchIndex >= 0 && (lowestSearchIndex == null || searchIndex < lowestSearchIndex)) {
        lowestSearchIndex = searchIndex;
        selectIndex = index;
      }
      let isMatch = searchIndex >= 0;
      return isMatch;
    });
    let $filtered = $itemList.not($matches);

    this.triggerOnFilter(filterText, $matches, $filtered);
    this.showMatches($matches, filterText);
    this.hideFiltered($filtered);
    let $selected = this.setSelectedItem($matches, $itemList, selectIndex);

    this.triggerOnFiltered(filterText, $matches, $filtered, $selected);
  }

  public emptyFilter(): void {
    this.showMatches(this.$items);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  };

  private showMatches($items: JQuery, highlightFilter?: string): void {
    $items.removeClass(this.selectedClass)
      .addClass('fi-matched')
      .attr('data-fi-matched', 'on')
      .removeClass('fi-filtered')
      .removeAttr('data-fi-filtered')
      .unhighlight();

    if (highlightFilter)
      $items.highlight(highlightFilter);
  }

  private hideFiltered($items: JQuery): void {
    $items.removeClass(this.selectedClass)
      .addClass('fi-filtered')
      .attr('data-fi-filtered', 'on')
      .removeClass('fi-matched')
      .removeClass(this.selectedClass)
      .removeAttr('data-fi-matched')
      .unhighlight();
  }

  private setSelectedItem($matches: JQuery, $items: JQuery, selectIndex: number): JQuery {
    if ($matches.length == 0)
      return $();

    let $selected = $($items.get(selectIndex - 1));
    $selected.addClass(this.selectedClass);

    return $selected;
  }

  private triggerOnFilter(filterText: string, $matches: JQuery, $filtered: JQuery): void {
    let onFilterData: OnFilterEventObject = {
      filterText: filterText,
      $matched: $matches,
      $filtered: $filtered
    };
    this.input.$element.trigger('onfilter.rustig.filteritems', onFilterData);
  }

  private triggerOnFiltered(filterText: string, $matches: JQuery, $filtered: JQuery, $selected: JQuery): void {
    let onFilteredData: OnFilteredEventObject = {
      filterText: filterText,
      $matched: $matches,
      $filtered: $filtered,
      $selected: $selected
    };
    this.input.$element.trigger('onfiltered.rustig.filteritems', onFilteredData);
  }

  private static optionMap(data: any): FilterItemsOptions {
    return {
      selectedClass: data.fiSelectedClass
    };
  }
}

export interface FilterItemsOptions {
  selectedClass?: string;
}

export interface OnFilterEventObject {
  filterText: string,
  $matched: JQuery,
  $filtered: JQuery
}

export interface OnFilteredEventObject {
  filterText: string,
  $matched: JQuery,
  $filtered: JQuery,
  $selected: JQuery
}

function Plugin(option, args) {
  return this.each(function () {
    let $this = $(this);
    let data = $this.data('rustig.filteritems');
    let options = typeof option == 'object' && option;

    if (!data) {
      data = new FilterItems($this, options);
      $this.data('rustig.filteritems', data);
    }
    if (typeof option == 'string')
      data[option](args);
  });
}

$.fn.filteritems = Plugin;
